<template>
  <div class="p-0">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Eventos del Colegio</h1>
      <div class="text-sm text-gray-600">
        Total: {{ total }} eventos
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <span class="ml-3 text-gray-600">Cargando eventos...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error al cargar eventos</h3>
          <p class="text-sm text-red-700 mt-1">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Events Grid -->
    <div v-else-if="events.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="event in events"
        :key="event.id"
        class="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
      >
        <!-- Event Header -->
        <div class="p-6">
          <div class="flex justify-between items-start mb-4">
            <h3 class="text-lg font-semibold text-gray-800 line-clamp-2">
              {{ event.title }}
            </h3>
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="{
                'bg-green-100 text-green-800': event.status === 'scheduled',
                'bg-yellow-100 text-yellow-800': event.status === 'draft',
                'bg-red-100 text-red-800': event.status === 'cancelled'
              }"
            >
              {{ getStatusText(event.status) }}
            </span>
          </div>

          <!-- Event Details -->
          <div class="space-y-3">
            <!-- Date and Time -->
            <div class="flex items-center text-gray-600">
              <svg class="h-4 w-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <span class="text-sm">{{ formatDateTime(event.start_datetime) }}</span>
            </div>

            <!-- Duration -->
            <div class="flex items-center text-gray-600">
              <svg class="h-4 w-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span class="text-sm">{{ getEventDuration(event.start_datetime, event.end_datetime) }}</span>
            </div>

            <!-- Location -->
            <div class="flex items-center text-gray-600">
              <svg class="h-4 w-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <span class="text-sm">{{ event.location }}</span>
            </div>

            <!-- Location Type -->
            <div class="flex items-center text-gray-600">
              <svg class="h-4 w-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
              <span class="text-sm">{{ getLocationTypeText(event.location_type) }}</span>
            </div>

            <!-- Capacity and Registration -->
            <div class="flex items-center justify-between text-gray-600 pt-2 border-t border-gray-100">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                <span class="text-sm">{{ event.registration_count }}/{{ event.capacity }} inscritos</span>
              </div>
              <div v-if="event.requires_registration" class="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                Requiere inscripción
              </div>
            </div>
          </div>

          <!-- Event Actions -->
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex space-x-2">
              <button
                v-if="event.requires_registration"
                class="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                Confirmar asistencia
              </button>
              <button class="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200">
                Ver detalles
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No hay eventos disponibles</h3>
      <p class="mt-1 text-sm text-gray-500">No se encontraron eventos públicos para mostrar.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "EventosView",
  data() {
    return {
      events: [],
      total: 0,
      loading: true,
      error: null,
    };
  },
  mounted() {
    this.getEvents();
  },
  methods: {
    getEvents() {
      this.loading = true;
      this.error = null;

      this.$http.get('/events?status=scheduled&is_public=true')
        .then(response => {
          const data = response.data;
          this.events = data.events || [];
          this.total = data.total || 0;
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching events:', error);
          this.error = error.message || 'Error al cargar los eventos';
          this.events = [];
          this.total = 0;
          this.loading = false;
        });
    },
    formatDateTime(dateTimeString) {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getEventDuration(startDateTime, endDateTime) {
      const start = new Date(startDateTime);
      const end = new Date(endDateTime);
      const diffMs = end - start;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffHours > 0) {
        return diffMinutes > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffHours}h`;
      }
      return `${diffMinutes} minutos`;
    },
    getStatusText(status) {
      const statusMap = {
        scheduled: 'Programado',
        draft: 'Borrador',
        cancelled: 'Cancelado',
        completed: 'Completado'
      };
      return statusMap[status] || status;
    },
    getLocationTypeText(locationType) {
      const typeMap = {
        onsite: 'Presencial',
        online: 'En línea',
        hybrid: 'Híbrido'
      };
      return typeMap[locationType] || locationType;
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
